import { Flex, Tooltip, Typography } from "antd"
import React from "react"

import { type SETTINGS_PERMISSIONS_COLUMNS } from "../constants/columns.ts"
import { getActionExtras, getColKey } from "../helpers/utils.ts"
import { type ActionRecord, type PermissionsMode } from "../types.ts"
import type {
  AnyPermissionKey,
  PermissionDisplayMap,
  PermissionFeatures
} from "../types/permissions.types.ts"
import { PermissionsActionSwitch } from "./PermissionsActionSwitch.tsx"

const { Text } = Typography

type PermissionsActionRowProps = {
  parentKey: keyof PermissionFeatures
  subKey: string
  actions: ActionRecord
  columns: ReadonlyArray<(typeof SETTINGS_PERMISSIONS_COLUMNS)[number]>
  gridTemplateColumns: string
  isUpdating?: boolean
  displayMap?: PermissionDisplayMap | null
} & PermissionsMode

const stripPrefix = (action: string, subKey: string) =>
  action.startsWith(`${subKey}_`) ? action.slice(subKey.length + 1) : action

export const PermissionsActionRow = ({
  parentKey,
  subKey,
  actions,
  columns,
  gridTemplateColumns,
  isUpdating,
  displayMap,
  ...rest
}: PermissionsActionRowProps) => {
  const meta = displayMap?.[subKey as AnyPermissionKey]
  const modeProps: PermissionsMode = rest?.asForm
    ? { asForm: true }
    : { onItemChange: rest?.onItemChange }

  const extras = getActionExtras(actions)

  return (
    <div className={"border-b border-border"}>
      <div
        className={"grid py-1.5 px-4 items-center"}
        style={{ gridTemplateColumns }}
      >
        <Tooltip title={meta?.tooltip}>
          <Text ellipsis className={"capitalize w-fit"}>
            {meta?.name ?? subKey.replace(/_/g, " ")}
          </Text>
        </Tooltip>
        {columns.map((col) => {
          const actionEntry = Object.entries(actions).find(
            ([action]) => getColKey(action) === col.key
          )

          return (
            <Flex justify="center" key={col.key}>
              {actionEntry ? (
                <PermissionsActionSwitch
                  parentKey={parentKey}
                  displayMap={displayMap}
                  subKey={subKey}
                  actionKey={actionEntry[0]}
                  value={actionEntry[1] ?? false}
                  isUpdating={isUpdating}
                  {...modeProps}
                />
              ) : (
                <Text type="secondary">—</Text>
              )}
            </Flex>
          )
        })}
      </div>

      {extras?.length > 0 && (
        <Flex wrap gap={16} className={"pb-2 pl-8 pr-4"}>
          {extras.map(([actionKey, value]) => {
            const meta = displayMap?.[actionKey as AnyPermissionKey]
            return (
              <Flex key={actionKey} align="center" gap={8} className={"py-1"}>
                <Tooltip title={meta?.tooltip}>
                  <Text
                    type="secondary"
                    ellipsis
                    className={"capitalize text-xs"}
                  >
                    {meta?.name ??
                      stripPrefix(actionKey, subKey).replace(/_/g, " ")}
                  </Text>
                </Tooltip>
                <PermissionsActionSwitch
                  parentKey={parentKey}
                  displayMap={displayMap}
                  subKey={subKey}
                  actionKey={actionKey}
                  value={value ?? false}
                  isUpdating={isUpdating}
                  {...modeProps}
                />
              </Flex>
            )
          })}
        </Flex>
      )}
    </div>
  )
}
